import { Suspense, useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import axios, { AxiosError } from 'axios';
import { startCase, toLower } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'components/elements/Spinner';
import { StickyItem } from 'components/elements/StickyItem';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import MarketNotInSubscription from 'components/widgets/Error/MarketNotInSubscription';
import PageNotFound from 'components/widgets/Error/PageNotFound';
import SomethingWentWrong from 'components/widgets/Error/SomethingWentWrong';
import {
  TITLE_BAR_HEIGHT,
  CONTENT_TOP_PADDING,
} from 'components/widgets/ProjectDetails/const';
import PageHeader from 'components/widgets/ProjectDetails/PageHeader';
import ProjectDetailsBody from 'components/widgets/ProjectDetails/ProjectDetailsBody';
import SuggestCorrectionFooter from 'components/widgets/SuggestCorrection/Footer';
import { useAnalytics } from 'hooks/useAnalytics';
import { useDocumentDetailsForDDP } from 'hooks/useDocumentOverview';
import InvolvedCompanies from 'pages/ProjectDetails/InvolvedCompanies';
import Menu from 'pages/ProjectDetails/Menu';
import { useCurrentRegion } from 'state/currentRegion';
import { useMapState } from 'state/map';
import { ProjectDetailsPageViewSections } from 'utils/analytics/constants';
import { DOCUMENT_TYPES } from 'utils/constants/documentTypes';
import { Region } from 'utils/constants/location';
import { pdpUrl } from 'utils/navigation';

const pageContainerStyle = {
  overflowY: 'auto',
  height: '100%',
  position: 'relative',
};

const pageContentWrapperStyle = {
  paddingRight: (theme: any) => theme.space.fortyEight,
  paddingLeft: (theme: any) => theme.space.fortyEight,
  paddingBottom: (theme: any) => theme.space.fortyEight,
  paddingTop: CONTENT_TOP_PADDING,
};

const stickyMenuStyle = {
  zIndex: 3,
  top: `calc(${TITLE_BAR_HEIGHT} + ${CONTENT_TOP_PADDING})`,
};

const pageContentGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 3fr 1fr',
  gridTemplateRows: '100%',
  gap: '16px 16px',
  gridTemplateAreas: '. . .',
  maxWidth: '1440px',
  margin: 'auto',
};

const mainProjectInfoStyle = {
  maxWidth: '784px',
  width: '100%',
  margin: 'auto',
};

const additionalInformationColumnStyle = {
  rowGap: (theme: any) => theme.space.eight,
};

const DDP = ({ documentId }: { documentId: string }) => {
  const {
    project: ddpProject,
    isLoadingProject: isLoadingDDPProject,
    landTitleHistory,
    isLoadingLandTitleHistory,
    involvedCompanies,
    projectEvents,
    isLoadingProjectEvents,
    projectActivitySummary,
    isLoadingProjectActivitySummary,
    isErrorLoadingActivitySummary,
    isProjectsByAddressLoading,
    projectsList,
    numberOfProjectsByAddress,
    setPage,
  } = useDocumentDetailsForDDP(documentId);

  const { usePageViewTracking } = useAnalytics();
  const { scrollable, updateScrollable } = useMapState();
  const { setCurrentRegion } = useCurrentRegion();
  const navigate = useNavigate();

  usePageViewTracking(
    {
      sectionName: ProjectDetailsPageViewSections.PROJECT_DETAILS,
      subSectionName: documentId || undefined,
    },
    [window.location.pathname],
  );

  useEffect(() => {
    /**
     * If the document has a project attached to it, redirect to the PDP.
     *
     * Unless the document is a plat, do not redirect to PDP because Plats are
     * still useful as individual DDPs.
     */
    if (
      ddpProject?.document_type_code.toLowerCase() === DOCUMENT_TYPES.PLAT.toLowerCase()
    ) {
      return;
    }

    if (ddpProject?.project_slugs && ddpProject.project_slugs.length === 1) {
      const targetURL = pdpUrl({ projectSlug: ddpProject.project_slugs[0] });

      navigate(targetURL);
      // Force a page reload to ensure all PDP data is fetched fresh
      window.location.reload();
    }
  }, [ddpProject?.project_slugs, ddpProject?.document_type_code, navigate]);

  useEffect(() => {
    if (!scrollable) {
      updateScrollable(true);
    }
  }, [scrollable, updateScrollable]);

  useEffect(() => {
    if (ddpProject?.mercator_metropolitan_id) {
      setCurrentRegion(ddpProject.mercator_metropolitan_id as Region);
    }
  }, [ddpProject?.mercator_metropolitan_id, setCurrentRegion]);

  if (isLoadingDDPProject || !ddpProject) {
    return (
      <BasePageWithTopNav>
        <Spinner />
      </BasePageWithTopNav>
    );
  }

  if (!isLoadingDDPProject && ddpProject && axios.isAxiosError(ddpProject)) {
    const axiosError = ddpProject as AxiosError;
    if (axiosError?.response?.status === 403) {
      return <MarketNotInSubscription />;
    }
    if (axiosError?.response?.status === 404) {
      return <PageNotFound />;
    }

    return <SomethingWentWrong />;
  }

  return (
    <BasePageWithTopNav>
      <PageMetadata title={startCase(toLower(ddpProject?.address))} />
      <Box sx={pageContainerStyle}>
        <Suspense fallback={<Box>Suspend Loading...</Box>}>
          <PageHeader
            address={ddpProject?.address || ''}
            city={ddpProject?.city || ''}
            state={ddpProject?.state || ''}
            coordinates={
              ddpProject?.latitude && ddpProject?.longitude
                ? { lat: ddpProject?.latitude, lng: ddpProject?.longitude }
                : undefined
            }
            zipcode={ddpProject?.zipcode || ''}
          />
        </Suspense>
        <Box sx={pageContentWrapperStyle}>
          <Box sx={pageContentGridStyle}>
            <Box>
              <StickyItem sx={stickyMenuStyle} position="sticky">
                <Menu
                  documentId={ddpProject.id}
                  address={ddpProject.address}
                  city={ddpProject.city}
                />
              </StickyItem>
            </Box>
            <Box sx={mainProjectInfoStyle}>
              <ProjectDetailsBody
                project={ddpProject}
                isLoadingProject={isLoadingDDPProject}
                projectActivitySummary={projectActivitySummary}
                isLoadingProjectActivitySummary={isLoadingProjectActivitySummary}
                isErrorLoadingActivitySummary={isErrorLoadingActivitySummary}
                projectSizeInfo={ddpProject.size_info}
                landTitleHistory={landTitleHistory}
                isLoadingLandTitleHistory={isLoadingLandTitleHistory}
                isProjectsByAddressLoading={isProjectsByAddressLoading}
                projectsList={projectsList}
                numberOfProjectsByAddress={numberOfProjectsByAddress || 0}
                setPage={setPage}
                projectEvents={projectEvents}
                isLoadingProjectEvents={isLoadingProjectEvents}
              />
            </Box>

            <FlexColumn sx={additionalInformationColumnStyle}>
              <InvolvedCompanies involved={involvedCompanies || null} />
            </FlexColumn>
          </Box>
        </Box>
        <SuggestCorrectionFooter />
      </Box>
    </BasePageWithTopNav>
  );
};

export default DDP;
